const normalizeComparableLabel = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ');

export const isGenericOptionLabel = (value) => {
  const normalized = normalizeComparableLabel(value);

  if (!normalized) return true;

  return (
    normalized === 'opcion' ||
    normalized === 'opcion unica' ||
    normalized === 'default' ||
    normalized === 'default title' ||
    normalized === 'variante' ||
    normalized === 'sin variante' ||
    normalized === 'unico' ||
    normalized === 'unica' ||
    /^opcion\s+\d+$/.test(normalized) ||
    /^variante\s+\d+$/.test(normalized)
  );
};

const stripGenericOptionSuffix = (value) => {
  let current = String(value || '').trim();

  while (current) {
    const parenthesizedMatch = current.match(/^(.*)\(([^)]+)\)\s*$/);
    if (parenthesizedMatch && isGenericOptionLabel(parenthesizedMatch[2])) {
      current = parenthesizedMatch[1].trim();
      continue;
    }

    const dashedMatch = current.match(/^(.*?)(?:\s*[–—-]\s*)([^()–—-]+)\s*$/);
    if (dashedMatch && isGenericOptionLabel(dashedMatch[2])) {
      current = dashedMatch[1].trim();
      continue;
    }

    break;
  }

  return current.replace(/\s*[–—-]\s*$/, '').trim();
};

export const sanitizeProductDisplayName = (value, fallback = 'Producto') => {
  const cleaned = stripGenericOptionSuffix(value);
  return cleaned || fallback;
};

export const getCleanVariantLabel = (value) => {
  const label = String(value || '').trim();
  return isGenericOptionLabel(label) ? '' : label;
};

// El nombre real de la variante vive en attributes.variant_name (schema actual).
// Mantenemos fallback a .name por compatibilidad con datos mapeados en la tienda.
export const getVariantName = (variant) => {
  if (!variant || typeof variant !== 'object') return '';
  // Talles y colores: "Negro / M" tiene prioridad sobre el nombre libre.
  const colorName = String(variant?.colores?.nombre || variant?.color?.nombre || variant?.color_nombre || '').trim();
  const talle = String(variant?.talle || '').trim();
  const attributesLabel = [colorName, talle].filter(Boolean).join(' / ');
  if (attributesLabel) return attributesLabel;
  const fromAttrs = variant?.attributes?.variant_name;
  if (fromAttrs && String(fromAttrs).trim()) return String(fromAttrs).trim();
  const fromName = variant?.name;
  if (fromName && String(fromName).trim()) return String(fromName).trim();
  return '';
};

export const buildProductDisplayName = (baseName, variantName, options = {}) => {
  const fallback = options.fallback || 'Producto';
  const separator = options.separator === 'dash' ? 'dash' : 'parentheses';
  const productName = sanitizeProductDisplayName(baseName, fallback);
  const cleanVariant = getCleanVariantLabel(variantName);

  if (!cleanVariant) return productName;

  return separator === 'dash'
    ? `${productName} - ${cleanVariant}`
    : `${productName} (${cleanVariant})`;
};

export const getSaleItemDisplayName = (item, fallback = 'Producto') => {
  if (item && typeof item === 'object') {
    return sanitizeProductDisplayName(
      item.product_name_snapshot ||
        item.products?.name ||
        item.producto?.name ||
        item.name,
      fallback,
    );
  }

  return sanitizeProductDisplayName(item, fallback);
};

export const hasMeaningfulVariantOptions = (variants) =>
  (Array.isArray(variants) ? variants : []).some((variant) =>
    Boolean(
      getCleanVariantLabel(
        getVariantName(variant) || variant?.description || variant?.sku,
      ),
    ),
  );
